import { observable, action, runInAction, computed } from "mobx";

import { imageLoader } from "lib/imageLoader";

import CanvasBlock from "./models/CanvasBlock";
import { Block, RfeImage } from "./models";

export default class CanvasStore {
  @observable private previewImage: HTMLImageElement;
  @observable private previewBlocks: CanvasBlock[] = [];
  @observable private previewRatio: number = 1;
  @observable private selectedBlock?: CanvasBlock;

  private previewCanvas: HTMLCanvasElement;
  private previewContext: CanvasRenderingContext2D;

  private detailCanvas: HTMLCanvasElement;
  private detailContext: CanvasRenderingContext2D;

  private selectionColor: string = "#C00";
  private selectionWidth: number = 2;

  private stylePaddingLeft: number;
  private stylePaddingTop: number;
  private styleBorderLeft: number;
  private styleBorderTop: number;
  private htmlTop: number;
  private htmlLeft: number;

  constructor() {
    if (document.defaultView && document.defaultView.getComputedStyle) {
      this.stylePaddingLeft = this.getComputedStyle("paddingLeft");
      this.stylePaddingTop = this.getComputedStyle("paddingTop");
      this.styleBorderLeft = this.getComputedStyle("borderLeftWidth");
      this.styleBorderTop = this.getComputedStyle("borderTopWidth");
    }
    const html = document.body.parentNode;
    this.htmlTop = (html as HTMLElement)!.offsetTop || 0;
    this.htmlLeft = (html as HTMLElement)!.offsetLeft || 0;

    window.addEventListener("resize", this.renderPreview, false);
  }

  @computed
  get selected() {
    return this.selectedBlock;
  }

  @computed
  get selectedId(): number | undefined {
    return this.selectedBlock ? Number(this.selectedBlock.id) : undefined;
  }

  public initPreview(element: HTMLCanvasElement) {
    this.previewCanvas = element;
    if (element) {
      this.previewContext = element.getContext("2d")!;
      this.renderPreview();
      element.addEventListener("click", this.onBlockClick, false);
      element.addEventListener("selectstart", this.handleOnSelect, false);
    }
  }

  @action.bound
  public updateSelectedBlock(source?: Block) {
    runInAction(() => {
      this.selectedBlock = source
        ? new CanvasBlock({
            id: source.id.toString(),
            source,
            canvas: this.previewCanvas,
          })
        : undefined;
    });
  }

  @action.bound
  public updatePreview(image: RfeImage, blocks: Block[]) {
    runInAction(async () => {
      if (image && image.url) {
        this.previewImage = await imageLoader(image.url);
        this.resizePreview();
      }
      if (blocks) {
        this.previewBlocks = blocks.map(
          (source) =>
            new CanvasBlock({
              id: source.id.toString(),
              source,
              canvas: this.previewCanvas,
            }),
        );
      }
      this.renderPreview();
    });
  }

  public renderPreview = () => {
    if (!this.previewCanvas || !this.previewImage) {
      return;
    }
    this.resizePreview();
    const { width, height } = this.previewCanvas;
    this.previewContext.clearRect(0, 0, width, height);
    this.previewContext.drawImage(
      this.previewImage,
      0,
      0,
      this.previewImage.width,
      this.previewImage.height,
      0,
      0,
      width,
      height,
    );
    if (this.previewBlocks.length > 0) {
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d")!;
      canvas.width = width;
      canvas.height = height;
      this.previewBlocks.forEach((block) => {
        block.setRatio(this.previewRatio);
        if (block.isRenderable) {
          return;
        }
        if (this.selectedId === Number(block.id)) {
          const {
            x,
            y,
            width: selectedWidth,
            height: selectedHeight,
          } = block.calculated;
          context.fillStyle = "rgba(24, 144, 255, 0.2)";
          context.fillRect(x, y, selectedWidth, selectedHeight);
          context.strokeStyle = this.selectionColor;
          context.lineWidth = this.selectionWidth;
          context.strokeRect(x, y, selectedWidth, selectedHeight);
          this.renderDetail();
        }
        block.stroke(context);
      });
      this.previewContext.drawImage(canvas, 0, 0);
    }
  };

  public initDetail(element: HTMLCanvasElement) {
    this.detailCanvas = element;
    if (element) {
      this.detailContext = element.getContext("2d")!;
      element.addEventListener("selectstart", this.handleOnSelect, false);
    }
  }

  public renderDetail() {
    if (!this.detailCanvas) {
      return;
    }
    this.resizeDetail();
    const { width, height } = this.detailCanvas;
    this.detailContext.clearRect(0, 0, width, height);
    const {
      x,
      y,
      width: blockWidth,
      height: blockHeight,
    } = this.selectedBlock!;
    this.detailContext.drawImage(
      this.previewImage,
      x,
      y,
      blockWidth,
      blockHeight,
      0,
      0,
      width,
      height,
    );
  }

  public onBlockClick = (e: MouseEvent) => {
    const { mouseX, mouseY } = this.getMouse(e);
    this.previewBlocks.forEach((element: CanvasBlock) => {
      if (element.contains(mouseX, mouseY)) {
        runInAction(() => {
          element.setChecked(true);
          this.selectedBlock = element;
        });
        return;
      }
    });
    this.renderPreview();
  };

  private handleOnSelect = (e: MouseEvent) => {
    e.preventDefault();
    return false;
  };

  private getMouse = (e: MouseEvent) => {
    let offsetX = 0;
    let offsetY = 0;

    if (this.previewCanvas.offsetParent !== undefined) {
      let element: any = this.previewCanvas;
      do {
        offsetX += element.offsetLeft;
        offsetY += element.offsetTop;
        if (!element || !element.offsetParent) {
          break;
        }
        element = element.offsetParent;
      } while (element && element.offsetParent !== undefined);
    }
    offsetX += this.stylePaddingLeft + this.styleBorderLeft + this.htmlLeft;
    offsetY += this.stylePaddingTop + this.styleBorderTop + this.htmlTop;

    const mouseX = e.pageX - offsetX;
    const mouseY = e.pageY - offsetY;

    return { mouseX, mouseY };
  };

  private getComputedStyle = (key: string, node?: Element): number => {
    if (!node || !this.previewCanvas) {
      return 0;
    }
    return (
      parseInt(
        document.defaultView!.getComputedStyle(
          node || this.previewCanvas,
          null,
        )![key],
        10,
      ) || 0
    );
  };

  private resizePreview = () => {
    const aspectRatio = this.previewImage
      ? this.previewImage.height / this.previewImage.width
      : 11 / 8.5;
    let width = this.previewCanvas.width;
    if (this.previewCanvas.offsetParent) {
      const paddingLeft = this.getComputedStyle(
        "paddingLeft",
        this.previewCanvas.offsetParent,
      );
      const paddingRight = this.getComputedStyle(
        "paddingRight",
        this.previewCanvas.offsetParent,
      );
      width =
        this.previewCanvas.offsetParent.clientWidth -
        paddingLeft -
        paddingRight;
    }
    const height = Math.floor(width * aspectRatio);
    this.previewRatio = width / this.previewImage.width;
    this.previewCanvas.width = width;
    this.previewCanvas.height = height;
  };

  private resizeDetail = () => {
    if (this.selectedBlock) {
      const aspectRatio = this.selectedBlock.height / this.selectedBlock.width;
      let width = this.detailCanvas.width;
      if (this.detailCanvas.offsetParent) {
        const paddingLeft = this.getComputedStyle(
          "paddingLeft",
          this.detailCanvas.offsetParent,
        );
        const paddingRight = this.getComputedStyle(
          "paddingRight",
          this.detailCanvas.offsetParent,
        );
        width =
          this.detailCanvas.offsetParent.clientWidth -
          paddingLeft -
          paddingRight;
      }
      const height = Math.floor(width * aspectRatio);
      this.detailCanvas.width = width;
      this.detailCanvas.height = height;
    }
  };
}
